import { Bot, Plus, Trash2 } from "lucide-react";
import { DiscordChannel, DiscordRole, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ChannelPicker } from "@/components/panel/ChannelPicker";
import { MultiSelect } from "@/components/MultiSelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type CustomCommandsConfig = GuildConfig["custom_commands"];
type CustomCommand = CustomCommandsConfig["commands"][number];
type AutoResponse = CustomCommandsConfig["auto_responses"][number];

type Props = {
  config: GuildConfig;
  channels: DiscordChannel[];
  roles: DiscordRole[];
  onChange: (v: CustomCommandsConfig) => void;
};

const emptyCommand = (): CustomCommand => ({
  id: `cc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  enabled: true,
  trigger: "rules",
  response: "Read the rules here: {rules_channel}",
  match_mode: "exact",
  reply_mode: "reply",
  delete_trigger: false,
  cooldown_seconds: 10,
  allowed_channel_ids: [],
  allowed_role_ids: [],
});

const emptyAutoResponse = (): AutoResponse => ({
  id: `ar_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  enabled: true,
  name: "Whitelist help",
  keywords: ["whitelist", "wl"],
  response: "Whitelist applications are here: {wl_channel}",
  match_mode: "contains",
  reply_mode: "reply",
  delete_trigger: false,
  cooldown_seconds: 60,
  allowed_channel_ids: [],
  ignored_channel_ids: [],
});

export function CustomCommandsTab({ config, channels, roles, onChange }: Props) {
  const cfg = config.custom_commands ?? {
    enabled: false,
    prefix: "!",
    log_channel_id: "",
    commands: [],
    auto_responses: [],
  };
  const textChannels = channels.filter((ch) => ch.type === 0);
  const channelOptions = textChannels.map((ch) => ({ id: ch.id, name: ch.name }));
  const roleOptions = roles.map((r) => ({ id: r.id, name: r.name, color: r.color }));

  const update = <K extends keyof CustomCommandsConfig>(key: K, value: CustomCommandsConfig[K]) => {
    onChange({ ...cfg, [key]: value });
  };

  const updateCommand = (idx: number, patch: Partial<CustomCommand>) => {
    const commands = [...(cfg.commands || [])];
    commands[idx] = { ...commands[idx], ...patch };
    update("commands", commands);
  };

  const updateAutoResponse = (idx: number, patch: Partial<AutoResponse>) => {
    const autoResponses = [...(cfg.auto_responses || [])];
    autoResponses[idx] = { ...autoResponses[idx], ...patch };
    update("auto_responses", autoResponses);
  };

  return (
    <Section
      icon={Bot}
      title="Custom Commands / Auto Responses"
      description="Advanced text commands and keyword replies for your server. Supports variables: {server}, {user}, {user_mention}, {channel}, {wl_channel}, {rules_channel}, {connect}."
      enabled={cfg.enabled}
      onToggle={(v) => update("enabled", v)}
    >
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">How it works</p>
        <p>
          <strong>Text commands:</strong> members type <code className="text-primary">{cfg.prefix || "!"}</code>
          + trigger (e.g. <code className="text-primary">rules</code>) per your match mode.{" "}
          <strong>Auto responses:</strong> trigger on keywords without a prefix. This is separate from Discord slash
          commands. Variables in responses are listed in the section description above.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Command prefix" hint="Example: !rules, ?ip. Slash commands stay separate.">
          <Input
            value={cfg.prefix}
            maxLength={5}
            onChange={(e) => update("prefix", e.target.value || "!")}
            placeholder="!"
          />
        </Field>
        <Field label="Log channel" hint="Optional. Bot logs when a custom command is triggered.">
          <ChannelPicker
            channels={textChannels}
            value={cfg.log_channel_id}
            onChange={(v) => update("log_channel_id", v === "__none__" ? "" : v)}
          />
        </Field>
      </div>

      <div className="space-y-3 pt-3 border-t border-border">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold">Text commands</h3>
            <p className="text-xs text-muted-foreground">
              Members type your prefix plus trigger, for example {cfg.prefix || "!"}rules.
            </p>
          </div>
          <Button size="sm" onClick={() => update("commands", [...(cfg.commands || []), emptyCommand()])}>
            <Plus className="h-4 w-4" />
            Add command
          </Button>
        </div>

        {(cfg.commands || []).map((cmd, idx) => (
          <div key={cmd.id || idx} className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Switch checked={cmd.enabled} onCheckedChange={(v) => updateCommand(idx, { enabled: v })} />
              <Input
                value={cmd.trigger}
                onChange={(e) => updateCommand(idx, { trigger: e.target.value })}
                placeholder="rules"
              />
              <Button
                size="icon"
                variant="ghost"
                onClick={() => update("commands", (cfg.commands || []).filter((_, i) => i !== idx))}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>

            <Field label="Response">
              <Textarea
                rows={3}
                value={cmd.response}
                onChange={(e) => updateCommand(idx, { response: e.target.value })}
                placeholder="Write the bot response..."
              />
            </Field>

            <div className="grid md:grid-cols-3 gap-3">
              <Field label="Match">
                <Select
                  value={cmd.match_mode}
                  onValueChange={(v) => updateCommand(idx, { match_mode: v as CustomCommand["match_mode"] })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="exact">Exact</SelectItem>
                    <SelectItem value="starts_with">Starts with</SelectItem>
                    <SelectItem value="contains">Contains</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Reply mode">
                <Select
                  value={cmd.reply_mode}
                  onValueChange={(v) => updateCommand(idx, { reply_mode: v as CustomCommand["reply_mode"] })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reply">Reply to user</SelectItem>
                    <SelectItem value="channel">Send in channel</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Cooldown seconds">
                <Input
                  type="number"
                  min={0}
                  max={86400}
                  value={cmd.cooldown_seconds}
                  onChange={(e) => updateCommand(idx, { cooldown_seconds: Math.max(0, Number(e.target.value) || 0) })}
                />
              </Field>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Allowed channels" hint="Empty = all text channels.">
                <MultiSelect
                  options={channelOptions}
                  value={cmd.allowed_channel_ids}
                  onChange={(ids) => updateCommand(idx, { allowed_channel_ids: ids })}
                  prefix="#"
                  placeholder="+ channel"
                />
              </Field>
              <Field label="Allowed roles" hint="Empty = everyone can use it.">
                <MultiSelect
                  options={roleOptions}
                  value={cmd.allowed_role_ids}
                  onChange={(ids) => updateCommand(idx, { allowed_role_ids: ids })}
                  prefix="@"
                  placeholder="+ role"
                />
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={cmd.delete_trigger} onCheckedChange={(v) => updateCommand(idx, { delete_trigger: v })} />
              Delete the user trigger message
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={!!cmd.allow_mentions}
                onCheckedChange={(v) => updateCommand(idx, { allow_mentions: v })}
              />
              Allow @everyone / @here / role / user pings in the response
            </label>
            {cmd.allow_mentions && (
              <p className="text-xs text-amber-500 pl-[3.25rem] -mt-2">
                ⚠ Anyone who can trigger this command can ping everyone. Restrict allowed roles above if needed.
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="space-y-3 pt-3 border-t border-border">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold">Auto responses</h3>
            <p className="text-xs text-muted-foreground">
              Bot replies when configured keywords appear in chat.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => update("auto_responses", [...(cfg.auto_responses || []), emptyAutoResponse()])}
          >
            <Plus className="h-4 w-4" />
            Add response
          </Button>
        </div>

        {(cfg.auto_responses || []).map((res, idx) => (
          <div key={res.id || idx} className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Switch checked={res.enabled} onCheckedChange={(v) => updateAutoResponse(idx, { enabled: v })} />
              <Input
                value={res.name}
                onChange={(e) => updateAutoResponse(idx, { name: e.target.value })}
                placeholder="Response name"
              />
              <Button
                size="icon"
                variant="ghost"
                onClick={() => update("auto_responses", (cfg.auto_responses || []).filter((_, i) => i !== idx))}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>

            <Field label="Keywords" hint="Comma-separated words or phrases.">
              <Input
                value={(res.keywords || []).join(", ")}
                onChange={(e) =>
                  updateAutoResponse(idx, {
                    keywords: e.target.value.split(",").map((x) => x.trim()).filter(Boolean),
                  })
                }
                placeholder="whitelist, wl, apply"
              />
            </Field>

            <Field label="Response">
              <Textarea
                rows={3}
                value={res.response}
                onChange={(e) => updateAutoResponse(idx, { response: e.target.value })}
                placeholder="Write the automatic reply..."
              />
            </Field>

            <div className="grid md:grid-cols-3 gap-3">
              <Field label="Match">
                <Select
                  value={res.match_mode}
                  onValueChange={(v) => updateAutoResponse(idx, { match_mode: v as AutoResponse["match_mode"] })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contains">Contains</SelectItem>
                    <SelectItem value="word">Whole word</SelectItem>
                    <SelectItem value="exact">Exact message</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Reply mode">
                <Select
                  value={res.reply_mode}
                  onValueChange={(v) => updateAutoResponse(idx, { reply_mode: v as AutoResponse["reply_mode"] })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reply">Reply to user</SelectItem>
                    <SelectItem value="channel">Send in channel</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Cooldown seconds">
                <Input
                  type="number"
                  min={0}
                  max={86400}
                  value={res.cooldown_seconds}
                  onChange={(e) => updateAutoResponse(idx, { cooldown_seconds: Math.max(0, Number(e.target.value) || 0) })}
                />
              </Field>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Allowed channels" hint="Empty = all text channels.">
                <MultiSelect
                  options={channelOptions}
                  value={res.allowed_channel_ids}
                  onChange={(ids) => updateAutoResponse(idx, { allowed_channel_ids: ids })}
                  prefix="#"
                  placeholder="+ channel"
                />
              </Field>
              <Field label="Ignored channels">
                <MultiSelect
                  options={channelOptions}
                  value={res.ignored_channel_ids}
                  onChange={(ids) => updateAutoResponse(idx, { ignored_channel_ids: ids })}
                  prefix="#"
                  placeholder="+ channel"
                />
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={res.delete_trigger} onCheckedChange={(v) => updateAutoResponse(idx, { delete_trigger: v })} />
              Delete the user trigger message
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={!!res.allow_mentions}
                onCheckedChange={(v) => updateAutoResponse(idx, { allow_mentions: v })}
              />
              Allow @everyone / @here / role / user pings in the response
            </label>
            {res.allow_mentions && (
              <p className="text-xs text-amber-500 pl-[3.25rem] -mt-2">
                ⚠ Anyone whose message matches these keywords can trigger a ping to everyone. Use with caution.
              </p>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}
